import type { IUserProfile, IUserSession } from '@/service/auth/types';
import type { IInvitation } from '@/service/team/invitation';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface IAuthActions {
  /**
   * 入参不可空：清 profile 必须经 `clearAuth()`，只清这一个会留下陈旧 session，被轮询判成会话已被替换
   * 改用户信息的接口都返回完整 profile，必须整份写回，禁止挑字段、也禁止改成 `revalidate()` 重跑 loader
   */
  setProfile: (profile: IUserProfile) => void;
  setSession: (session?: IUserSession) => void;
  setInvite: (invite: IAuthState['invite']) => void;
  /** 登出清理：profile、session 与邀请通知必须一起丢。*/
  clearAuth: () => void;
}

interface IAuthState {
  /** 当前用户。由 `loader/profile.ts` 首次写入，之后由改用户信息的接口响应更新 */
  profile?: IUserProfile;
  session?: IUserSession;
  /** 待处理的团队邀请，由 `useSessionPolling` 从 `/auth/status` 的通知里取出后写入 */
  invite?: IInvitation;
  actions: IAuthActions;
}

export const useUserStore = create<IAuthState>()(
  immer((set) => ({
    profile: undefined,
    session: undefined,
    invite: undefined,
    actions: {
      setProfile: (profile) =>
        set((state) => {
          state.profile = profile;
        }),
      setSession: (session) =>
        set((state) => {
          state.session = session;
        }),
      setInvite: (invite) =>
        set((state) => {
          state.invite = invite;
        }),
      clearAuth: () =>
        set((state) => {
          state.profile = undefined;
          state.session = undefined;
          // 留着的话，同一标签页换账号登录后会先看到上一个账号的邀请卡片
          state.invite = undefined;
        })
    }
  }))
);

export const useSessionId = () => useUserStore((state) => state.session?.sessionId);

export const useInvite = () => useUserStore((state) => state.invite);

/**
 * 返回值可空，禁止改成抛错：根级 ErrorBoundary 也会渲染 `Header`（进而 `UserPopup`）
 * 那条路径上可能还没有任何用户数据
 */
export const useProfile = () => useUserStore((state) => state.profile);

export const useUserAction = () => useUserStore((state) => state.actions);
